import React from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	Button,
	Radio,
	RadioGroup,
	VStack,
	HStack,
	Text,
	Badge,
	Spinner,
} from '@chakra-ui/react';
import { useListEligibleChannelsQuery } from 'api/metaWhatsappApi';

/**
 * Choosing which number a new conversation goes out from.
 *
 * ★ THE LIST COMES FROM THE SERVER.
 * `/channels/eligible` returns what this user may actually start from, computed
 * server-side. Deriving it from the client permission map would be wrong twice:
 * the map is stale until reload, and permission alone does not imply the
 * channel is assigned, enabled and routable.
 *
 * ★ THERE IS NO SILENT DEFAULT.
 * When several numbers are eligible the server answers `409` rather than
 * picking one, because which number a customer sees is a business decision and
 * a wrong choice is not recoverable — the customer has already seen it. This
 * dialog is that decision.
 *
 * Chakra v1: `isOpen`/`onClose`, and `spacing` on stacks (not `gap`).
 */
export default function SenderSelect({ isOpen, onClose, onSelect, isSending }) {
	const { data: channels = [], isLoading } = useListEligibleChannelsQuery(undefined, {
		skip: !isOpen,
	});
	const [choice, setChoice] = React.useState('');

	// Pre-select the default when there is one, so the common case is one click.
	React.useEffect(() => {
		if (!choice && channels.length) {
			const preferred = channels.find((c) => c.isDefault) ?? channels[0];
			setChoice(preferred.phoneNumberId);
		}
	}, [channels, choice]);

	return (
		<Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
			<ModalOverlay />
			<ModalContent bg="bg.surface">
				<ModalHeader color="text.primary" fontSize="md">
					Send from which number?
				</ModalHeader>
				<ModalCloseButton />

				<ModalBody>
					{isLoading && <Spinner size="sm" />}

					{!isLoading && channels.length === 0 && (
						<Text fontSize="sm" color="text.muted">
							You are not assigned to any number that can start a conversation.
						</Text>
					)}

					{!isLoading && channels.length > 0 && (
						<RadioGroup value={choice} onChange={setChoice}>
							<VStack spacing="3" align="stretch">
								{channels.map((c) => (
									<Radio key={c.phoneNumberId} value={c.phoneNumberId}>
										<HStack spacing="2">
											<Text fontSize="sm" color="text.primary">
												{c.label || c.displayPhoneNumber}
											</Text>
											{c.isDefault && (
												<Badge colorScheme="orange" variant="subtle" fontSize="0.6rem">
													Default
												</Badge>
											)}
										</HStack>
										<Text fontSize="xs" color="text.muted">
											{c.displayPhoneNumber}
										</Text>
									</Radio>
								))}
							</VStack>
						</RadioGroup>
					)}
				</ModalBody>

				<ModalFooter>
					<Button size="sm" variant="ghost" mr="2" onClick={onClose}>
						Cancel
					</Button>
					<Button
						size="sm"
						colorScheme="green"
						onClick={() => onSelect(choice)}
						isDisabled={!choice}
						isLoading={isSending}
					>
						Start conversation
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
